import { DuplicateButton } from '../components/DuplicateButton'
import { RemoveButton } from '../components/RemoveButton'
import { SplitButton } from '../components/SplitButton'
import { EditButton } from '../components/EditButton'
import { useSelector } from 'react-redux'
import { editorSelectors } from 'store/features/editor/selectors'
import { FrameExtractButton } from '../components/FrameExtractButton'
import { useResponsive } from 'hooks/useResponsive'
import { imageTypes, MOBILE_SIZE_BREAKPOINT } from 'consts'
import MagnetButton from '../components/MagnetButton'
import { useActiveItems } from './use-active-items'
import VolumeControlButton from '../components/VolumeControlButton'
import AnimateButton from '../components/AnimateButton'
import { selectActiveMedia } from 'store/selectors'
import { useMemo } from 'react'

export interface IButtonData {
  key: string
  component: React.ReactElement
  isVisible: boolean
}

const hasVolumeForAllItems = <T,>(
  items: T[],
  getVolume: (item: T) => unknown
): boolean => items.every(item => getVolume(item) !== undefined)

export const useTimelineButtons = (): {
  primaryButtons: IButtonData[]
  secondaryButtons: IButtonData[]
} => {
  const isMobile = useResponsive(MOBILE_SIZE_BREAKPOINT)
  const isEditButtonVisible = useSelector(
    editorSelectors.isTimelineHeaderEditButtonVisible
  )
  const activeMediaItem = useSelector(selectActiveMedia)
  const { activeMediaItems, activeAudioItems } = useActiveItems()

  const hasVolumeControl = useMemo(() => {
    if (!activeMediaItems.length && !activeAudioItems.length) {
      return false
    }
    const hasVolumeForMediaItems = activeMediaItems.length
      ? hasVolumeForAllItems(activeMediaItems, item => item.meta?.volume)
      : true
    const hasVolumeForAudioItems = activeAudioItems.length
      ? hasVolumeForAllItems(activeAudioItems, item => item.volume)
      : true
    return hasVolumeForMediaItems && hasVolumeForAudioItems
  }, [activeMediaItems, activeAudioItems])

  const isImageType = useMemo(
    () => Boolean(activeMediaItem && imageTypes.includes(activeMediaItem.type)),
    [activeMediaItem]
  )

  const primary = useMemo(
    () => [
      {
        key: 'edit',
        component: <EditButton />,
        isVisible: isEditButtonVisible
      },
      {
        key: 'split',
        component: <SplitButton />,
        isVisible: true
      },
      {
        key: 'frameExtract',
        component: <FrameExtractButton />,
        isVisible: true
      }
    ],
    [isEditButtonVisible]
  )

  const additionalButtons = useMemo(
    () => [
      {
        key: 'volume',
        isVisible: hasVolumeControl,
        component: <VolumeControlButton />
      },
      {
        key: 'animate',
        isVisible: isImageType,
        component: <AnimateButton />
      }
    ],
    [hasVolumeControl, isImageType]
  )

  const secondary = useMemo(
    () => [
      {
        key: 'delete',
        component: <RemoveButton />,
        isVisible: true
      },
      {
        key: 'duplicate',
        component: <DuplicateButton />,
        isVisible: true
      },
      {
        key: 'magnet',
        component: <MagnetButton />,
        isVisible: true
      }
    ],
    []
  )

  const webButtons = useMemo(
    () => [...primary, ...secondary, ...additionalButtons],
    [primary, secondary, additionalButtons]
  )

  return isMobile
    ? {
        primaryButtons: [...primary, ...additionalButtons],
        secondaryButtons: secondary
      }
    : {
        primaryButtons: webButtons,
        secondaryButtons: []
      }
}
